<template>
    <rkm-area
        title="Paciente"
        type="fieldset"
    >
        <rkm-row>
            <rkm-col>
                <h4>
                    {{ data.form.paciente.nome }}
                    <strong v-if="data.form.paciente.nomeSocial">
                        ({{ data.form.paciente.nomeSocial }})
                    </strong>

                    <span
                        v-if="data.form.paciente.obitoEm"
                        class="ml-1"
                        title="Paciente em óbito"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="cross" />
                    </span>

                    <span
                        v-if="data.form.paciente.bloqueado"
                        class="ml-1"
                        title="Cadastro bloqueado"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="ban" />
                    </span>

                    <span
                        v-if="isProvisorioOuTemporario"
                        class="ml-1"
                        title="Paciente provisório/temporário"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="hourglass-half" />
                    </span>

                    <span
                        v-if="data.form.paciente.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                        class="ml-1"
                        title="Paciente não munícipe"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="map-pin" />
                    </span>

                    <span
                        v-if="!data.form.paciente.cpf && !data.form.paciente.cns"
                        class="ml-1"
                        title="Paciente sem CPF e CNS"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="exclamation-triangle" />
                    </span>

                    <span
                        v-if="data.form.paciente.responsavelFamiliar"
                        class="ml-1"
                        title="Responsável familiar"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="crown" />
                    </span>

                    <span
                        v-if="isIdoso"
                        class="ml-1"
                        title="Idoso"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="blind" />
                    </span>

                    <span
                        v-if="isTea"
                        class="ml-1"
                        title="Pessoa com Transtorno do Espectro Autista - TEA"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="infinity" />
                    </span>

                    <span
                        v-if="isPcd"
                        class="ml-1"
                        title="Pessoa com Deficiência - PCD"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="wheelchair" />
                    </span>
                </h4>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <p>
                    <strong>Prontuário:</strong>
                    {{ data.form.paciente.prontuario }}
                </p>
                <p>
                    <strong>CPF:</strong>
                    {{ data.form.paciente.cpf }}
                </p>
                <p>
                    <strong>CNS:</strong>
                    {{ data.form.paciente.cns }}
                </p>
                <p>
                    <strong>RG:</strong>
                    {{ data.form.paciente.rg || '-' }}
                </p>
                <p>
                    <strong>Telefone:</strong>
                    <template v-if="data.form.paciente.telefoneNumero">
                        {{ data.form.paciente.telefoneNumero | formatPhoneOrCellPhone }}
                    </template>
                    <template v-else>
                        -
                    </template>
                    <rkm-button
                        v-if="verificarPermissao('update-cadastro-paciente')"
                        @click="$refs.ModalTelefone.open(data.form.paciente)"
                        class="ml-1"
                        color="primary"
                        title="Alterar telefone"
                        v-rkm-tooltip.hover
                        small
                    >
                        <rkm-icon icon="pen" />
                    </rkm-button>
                </p>
            </rkm-col>

            <rkm-col cols="4">
                <p>
                    <strong>Data de nascimento:</strong>
                    {{ data.form.paciente.dataNascimento }}
                </p>
                <p>
                    <strong>Idade:</strong>
                    {{ data.form.paciente.idade }}
                </p>
                <p>
                    <strong>Sexo:</strong>
                    {{ data.form.paciente.sexo || '-' }}
                </p>
                <p>
                    <strong>Nome da mãe:</strong>
                    <template v-if="data.form.paciente.nomeMae">
                        {{ data.form.paciente.nomeMae }}
                    </template>
                    <template v-else>
                        <i>Desconhecido</i>
                    </template>
                </p>
            </rkm-col>

            <rkm-col cols="4">
                <p>
                    <strong>Classificação:</strong>
                    {{ classificacaoDescricao }}
                </p>
                <p>
                    <strong>Unidade de referência:</strong>
                    <template v-if="data.form.paciente.unidadeReferencia">
                        {{ data.form.paciente.unidadeReferencia }}
                    </template>
                    <template v-else>
                        <i>Não informado no cadastro do paciente</i>
                    </template>
                </p>
                <p>
                    <strong>Área e Microárea:</strong>
                    {{ data.form.paciente.area || 'Não Informada' }} / {{ data.form.paciente.microarea || 'Não Informada' }}
                </p>
                <p>
                    <strong>INE:</strong>
                    {{ data.form.paciente.ine || 'Não Informado' }}
                </p>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <p>
                    <strong>Endereço:</strong>
                    {{ data.form.paciente.endereco || 'Não informado' }}
                </p>
            </rkm-col>
        </rkm-row>

        <ModalTelefone
            @saved="salvarTelefone"
            ref="ModalTelefone"
        />

        <template v-if="data.model">
            <hr>

            <rkm-form ref="Form">
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :disabled="!data.additionals.permiteEdicao"
                            :validations="validations.prontuarioUnidade"
                            label="Prontuário da unidade"
                            uppercase
                            v-model="data.model.prontuarioUnidade"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :disabled="!data.additionals.permiteEdicao"
                            :validations="validations.peso"
                            @input="handlePesoInput"
                            label="Peso"
                            v-mask="mask.peso"
                            v-model="data.model.peso"
                        >
                            <template slot="append">
                                <rkm-button
                                    :disabled="!data.additionals.permiteEdicao"
                                    :color="data.model.pesoUnidadeMedidaId === enums.unidadeMedida.QUILOGRAMAS ? 'secondary' : 'light'"
                                    @click="setUnidadeMedida('peso', enums.unidadeMedida.QUILOGRAMAS)"
                                >
                                    {{ getUnidadeMedida(enums.unidadeMedida.QUILOGRAMAS).sigla }}
                                </rkm-button>

                                <rkm-button
                                    :disabled="!data.additionals.permiteEdicao"
                                    :color="data.model.pesoUnidadeMedidaId === enums.unidadeMedida.GRAMAS ? 'secondary' : 'light'"
                                    @click="setUnidadeMedida('peso', enums.unidadeMedida.GRAMAS)"
                                >
                                    {{ getUnidadeMedida(enums.unidadeMedida.GRAMAS).sigla }}
                                </rkm-button>
                            </template>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :disabled="!data.additionals.permiteEdicao"
                            :validations="validations.altura"
                            @input="handleAlturaInput"
                            label="Altura"
                            v-mask="mask.altura"
                            v-model="data.model.altura"
                        >
                            <template slot="append">
                                <rkm-button
                                    :disabled="!data.additionals.permiteEdicao"
                                    :color="data.model.alturaUnidadeMedidaId === enums.unidadeMedida.METROS ? 'secondary' : 'light'"
                                    @click="setUnidadeMedida('altura', enums.unidadeMedida.METROS)"
                                >
                                    {{ getUnidadeMedida(enums.unidadeMedida.METROS).sigla }}
                                </rkm-button>

                                <rkm-button
                                    :disabled="!data.additionals.permiteEdicao"
                                    :color="data.model.alturaUnidadeMedidaId === enums.unidadeMedida.CENTIMETROS ? 'secondary' : 'light'"
                                    @click="setUnidadeMedida('altura', enums.unidadeMedida.CENTIMETROS)"
                                >
                                    {{ getUnidadeMedida(enums.unidadeMedida.CENTIMETROS).sigla }}
                                </rkm-button>
                            </template>
                        </rkm-form-input>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </template>
    </rkm-area>
</template>

<script>
// Components.
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Enums.
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Filters.
import formats from '@/filters/formats'

// Mixins.
import { error, permissao, repository, stringCase } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: { ModalTelefone },
    computed: {
        classificacaoDescricao() {

            const paciente = this.data.form.paciente

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {

                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {

                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isProvisorioOuTemporario() {

            return this.data.form.paciente.classificacaoId == this.enums.classificacao.PROVISORIO
                || this.data.form.paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso() {

            const dataNascimento = this.data.form.paciente.dataNascimento

            return !!dataNascimento && moment().diff(moment(dataNascimento, 'DD/MM/YYYY'), 'years') >= 60
        },
        isTea() {

            const ids = this.data.form.paciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.data.form.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {

            const ids = this.data.form.paciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.data.form.paciente.deficienciaId && this.data.form.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        validations() {

            return {
                prontuarioUnidade: { max: 10 },
                peso: {},
                altura: {},
            }
        }
    },
    created() {

        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        }
    },
    data: () => ({
        enums: { unidadeMedida, classificacao, subclassificacao, deficienciaBase, deficiencia },
        mask: {
            altura: '###',
            peso: '######',
        },
    }),
    filters: { ...formats },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        salvarTelefone() {

            this.setLoading()

            this.repositories.paciente.telefone(this.data.form.paciente.id, this.camelObjectKeysToSnakeRecursive({
                numero: this.data.form.paciente.telefoneNumero,
                autorizaSms: this.data.form.paciente.telefoneAutorizaSms,
            }))
                .then(() => this.showSaveNotification())
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        validate() {

            return this.$refs.Form.validate()
        },
        handlePesoInput(item) {

            const length = item.replace('.', '').length

            if (this.data.model.pesoUnidadeMedidaId === this.enums.unidadeMedida.QUILOGRAMAS) {

                if (length <= 3) {

                    this.mask.peso = '#.##'
                }
                else if (length <= 4) {

                    this.mask.peso = '##.##'
                }
                else {

                    this.mask.peso = '###.##'
                }
            }
            else {

                this.mask.peso = '######'
            }
        },
        handleAlturaInput() {

            if (this.data.model.alturaUnidadeMedidaId === this.enums.unidadeMedida.METROS) {

                this.mask.altura = '#.##'
            }
            else {

                this.mask.altura = '###'
            }
        },
        getUnidadeMedida(id) {

            return this.data.form.unidadesMedida.find(unidade => unidade.id === id)
        },
        setUnidadeMedida(key, destinoId) {

            if (this.data.model[`${key}UnidadeMedidaId`] == destinoId) return false

            this.$nextTick(() => {

                this.data.model[`${key}UnidadeMedidaId`] = destinoId
            })
        },
    },
    mixins: [error, permissao, repository, stringCase],
    props: {
        data: { type: Object, required: true },
    },
    watch: {
        'data.model.pesoUnidadeMedidaId'() {

            this.$nextTick(() => {

                this.data.model.peso = ''
            })
        },
        'data.model.alturaUnidadeMedidaId'() {

            this.$nextTick(() => {

                this.data.model.altura = ''
            })
        },
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}
</style>
